// Shared types + fetchers for the stats endpoints. Two callers — the
// account Stats tab (full payload) and the public profile page (slice).
// Both go through `api()` so auth + csrf + error shape are consistent.

import { api } from './api';

export interface StatsTotals {
  rounds_played: number;
  modes_completed: number;
  first_played: string | null;
  last_played: string | null;
}

/** One row of the per-filter streak breakdown surfaced on the profile's
 *  Practice Countries card. mode_id ride-along so a future multi-mode
 *  region-practice can disambiguate two filters with the same slug. */
export interface PracticeCountriesPerFilter {
  mode_id: string;
  /** URL filter slug — '' for unfiltered, 'africa', 'europe', etc. */
  filter_slug: string;
  streak_best: number;
}

/** A single quiz run's best-of comparison shape. correct/total are
 *  first-try-correct counts; ms is the total wall-clock for the run. */
export interface QuizBestRun {
  correct: number;
  total: number;
  ms: number;
}

/** One row of the quiz card's per-continent table. `best_run` is null
 *  when the player has no non-legacy runs for this mode_id (legacy =
 *  rounds with no first_try data; aggregator excludes them so the
 *  display shows "—" until a fresh run lands). */
export interface QuizPerMode {
  mode_id: string;
  runs: number;
  best_run: QuizBestRun | null;
  last_played: string;
}

export interface StatsByFormat {
  daily: { runs: number; best_total_score: number | null; median_total_score: number | null };
  /** City-distance practice. `best_km` = closest single guess across history. */
  practice_cities: { rounds: number; median_km: number | null; best_km: number | null };
  /** Region practice. `streak_best` = max across all (mode, filter) rows;
   *  `per_filter` is the sorted breakdown for the profile card. */
  practice_countries: {
    rounds: number;
    streak_best: number | null;
    per_filter: PracticeCountriesPerFilter[];
  };
  /** Quiz format. `runs` is the total run count across all quiz modes;
   *  `per_mode` is the per-continent table the SPA renders inside the
   *  Quiz card. */
  quiz: { runs: number; per_mode: QuizPerMode[] };
}

export interface StatsByMode {
  mode_id: string;
  format: 'daily' | 'practice' | 'quiz';
  runs: number;
  last_played: string;
  /** Daily mode — best total score (highest across runs). */
  best_total_score?: number;
  /** Practice (city-distance) — median guess distance in km. */
  median_km?: number;
  /** Quiz mode — best run by (correct DESC, ms ASC). Replaces best_ms. */
  best_run?: QuizBestRun | null;
  /** Region practice — best-ever consecutive-correct streak across all
   *  filters for this mode_id. */
  streak_best?: number;
}

export interface StatsStreak {
  current_days: number;
  best_days: number;
}

/**
 * One row in the recent-dailies history table. Mirrors the leaderboard's
 * row shape (per-round scores + total + avg) so the SPA can reuse the
 * same emoji + score-cell ribbon styling on stats / profile pages.
 */
export interface RecentDailyEntry {
  /** Local date the puzzle was played for, YYYY-MM-DD. */
  date: string;
  /** Per-round 0..100 scores in play order. */
  scores: number[];
  total_score: number;
  total_km: number;
  avg_km: number;
}

/**
 * Full stats payload returned by GET /api/account/stats. The public
 * endpoint omits `by_mode` and `streak`.
 */
export interface FullStats {
  totals: StatsTotals;
  by_format: StatsByFormat;
  by_mode: StatsByMode[];
  recent_dailies: RecentDailyEntry[];
  streak: StatsStreak;
}

export type PublicStats = Pick<FullStats, 'totals' | 'by_format' | 'recent_dailies'>;

interface AccountStatsResponse {
  stats: FullStats;
}

interface PublicStatsResponse {
  stats: PublicStats;
}

export async function fetchAccountStats(): Promise<FullStats> {
  const res = await api<AccountStatsResponse>('/api/account/stats');
  return res.stats;
}

export async function fetchPublicStats(username: string): Promise<PublicStats> {
  const res = await api<PublicStatsResponse>(
    `/api/users/${encodeURIComponent(username)}/stats`,
  );
  return res.stats;
}

/**
 * Format a millisecond total as `M:SS` clock-time. Used for quiz best-
 * time displays where ms is the raw unit but the reader wants
 * minutes:seconds. Sub-minute times still show with a leading 0:
 * (e.g. "0:42") so a column of times reads as a uniform clock,
 * not a mix of "Ss" and "Mm SSs".
 */
export function formatQuizMs(ms: number | null): string {
  if (ms === null) return '—';
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Format a `QuizBestRun` for display: "40/43 · 4:32". Used by the
 * stats Quiz card and the public profile to render the best-result
 * summary for each quiz mode. Null (legacy / never played) renders
 * as a single em-dash.
 */
export function formatQuizBest(best: QuizBestRun | null): string {
  if (best === null) return '—';
  return `${best.correct}/${best.total} · ${formatQuizMs(best.ms)}`;
}
